import { LoaderIcon, Sparkle, Upload, X } from 'lucide-react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { z } from 'zod';
import debounce from 'lodash.debounce';
import { useImagesContext } from 'features/gif-maker/context/ImagesContext';
import { gifService } from 'features/gif-maker/services/gifMakerApi';
import toast from 'react-hot-toast';
import { Input } from 'components/ui/input';
import endpoints from 'features/gif-maker/gifMakerEndPoints';
import axiosInstance from 'api/axios';
import axios from 'axios';

const isFileType = (input: any) => {
  return input instanceof File;
};

export interface SelectImagesPanelProps {
  onGifGenerated: (url: string) => void;
}

const SelectImagesPanel: React.FC<SelectImagesPanelProps> = ({
  onGifGenerated,
}) => {
  const {
    selectedImage,
    imageName,
    handleIsGenerating,
    setSelectedImage,
    clearSelectedImage,
  } = useImagesContext();
  const [isDragging, setIsDragging] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [button, setButton] = useState('Generate Gif');
  const [errors, setErrors] = useState({ image: false, prompt: false });
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [wsConnection, setWsConnection] = useState<WebSocket | null>(null);

  const MAX_FILE_SIZE_MB = 1;

  const urlSchema = z
    .string()
    .url()
    .regex(/\.(jpeg|jpg|png)$/i, 'URL must be a valid image (PNG, JPEG,JPG)');

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setSizeError(`File exceeds the 1 MB size limit.`);
      return null;
    }
    setSizeError(null);
    setSelectedImage(file, file.name);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleUrlValidation = debounce((url: string) => {
    const result = urlSchema.safeParse(url);
    if (result.success) {
      setUrlError(null);
      const name = url.split('/').pop() || 'Image';
      setSelectedImage(url, name);
    } else {
      setUrlError('Please enter a valid image URL (PNG, JPEG,JPG)');
    }
  }, 500);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
    };
  }, [wsConnection]);

  const checkTaskStatus = async (task_id: string) => {
    try {
      // Create WebSocket with protocols
      const ws = new WebSocket(
        `wss://api-fast.kreaitor.ai/api/v1/img-to-gif/ws/${task_id}`
      );

      // Add custom headers if needed
      ws.onopen = () => {
        console.log('WebSocket connection established');
      };

      ws.onmessage = (event) => {
        console.log('Raw message received:', event.data);
        try {
          const data = JSON.parse(event.data);
          console.log('Parsed message:', data);

          if (data.status === 'Completed' && data.gif_url) {
            onGifGenerated(data.gif_url);
            setButton('Generate Gif');
            handleIsGenerating(false);
            ws.close();
          } else if (data.status === 'Processing') {
            setButton('Generating GIF...');
          } else {
            // Log any other message type we receive
            console.log('Received message type:', data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);

        // Try to reconnect on error

        ws.close();
      };

      ws.onclose = (event) => {
        console.log('WebSocket connection closed:', event.code, event.reason);
      };

      setWsConnection(ws);
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      toast.error('Failed to connect to generation status service');
      setButton('Generate Gif');
      handleIsGenerating(false);
    }
  };

  const handleGenerateGif = async (e: any) => {
    if (!selectedImage) {
      if (!prompt) {
        setErrors({ prompt: true, image: true });
      }
      setErrors((prev) => ({ ...prev, image: true }));
      toast.error('Please Provide Image or Image URL');
      return;
    }
    if (!prompt) {
      setErrors((prev) => ({ ...prev, prompt: true }));
      toast.error('Please Enter Prompt');
      return;
    }
    setErrors({ image: false, prompt: false });
    e.preventDefault();
    handleIsGenerating(true);

    try {
      setButton('Generating Gif...');
      let imageUrl;

      if (isFileType(selectedImage)) {
        const file: File | string | null = selectedImage;
        const res = await gifService.generateImageUrl(
          file,
          abortControllerRef.current?.signal
        );
        imageUrl = String(res);
      } else {
        imageUrl = String(selectedImage);
      }

      const response = await axiosInstance.public.post(endpoints.GenerateGif, {
        image_url: imageUrl,
        prompt: prompt,
      });

      if (!response) {
        throw new Error(response || 'Failed to start GIF generation');
      }

      setTaskId(response.data.task_id);
      console.log('Task ID:', response.data.task_id);
      checkTaskStatus(response.data.task_id);
    } catch (error) {
      console.error('GIF generation error:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate GIF'
      );
      setButton('Generate Gif');
      handleIsGenerating(false);
    }
  };

  return (
    <div className="w-full bg-background rounded-lg p-10 mt-8">
      {sizeError ? <div className="text-red-500">{sizeError}</div> : ''}
      {selectedImage ? (
        <div className="text-center border border-stroke-regular rounded-sm">
          {typeof selectedImage === 'string' ? (
            <img
              src={selectedImage}
              alt="Uploaded"
              className="w-full max-h-64  object-contain rounded-lg mb-4"
            />
          ) : (
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Uploaded"
              className="w-full max-h-64 object-contain rounded-lg mb-4"
            />
          )}
          <div className="flex items-center w-full px-4 py-2 bg-background">
            <p className="text-base font-medium text-primary text-opacity-[62.75%]">
              {imageName}
            </p>
            <X
              onClick={clearSelectedImage}
              className="w-6 h-6 ml-auto text-text-primary cursor-pointer"
            />
          </div>
        </div>
      ) : (
        <>
          <div
            className={`flex flex-col items-center justify-center w-full h-44 border px-12 border-dashed text-center relative ${
              isDragging ? 'border-primary bg-primary' : 'border-primary'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <label
              htmlFor="fileInput"
              className="absolute inset-0 cursor-pointer"
            >
              <input
                id="fileInput"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            <Upload className="mx-auto h-[22px] w-[22px] text-text-primary" />
            <p className="mt-4 text-base font-medium text-text-primary">
              Drag and drop or click to choose a file
            </p>
            <p className="text-xs text-text-primary font-light">
              PNG, JPEG,JPG (UP TO 1MB)
            </p>
          </div>
          <div className="text-text-secondary flex justify-center items-center mt-[20px] ">
            <div className="border-t border-primary w-full"></div>
            <span className="text-primary font-medium mx-3">or</span>
            <div className="border-t border-primary w-full "></div>
          </div>
          <div className="mt-[30px]">
            <h3 className="text-center text-base font-medium text-text-primary">
              Enter image URL
            </h3>
            <Input
              type="text"
              className={`w-full mt-2 px-2 text-primary border bg-background border-opacity-[12.55%] rounded-md placeholder-text-placeholder outline-none ${
                urlError ? 'border-red-600' : 'border-secondary'
              }`}
              placeholder="Insert URL"
              onChange={(e) => handleUrlValidation(e.target.value)}
            />

            {urlError && (
              <p className="text-red-600 text-xs mt-2">{urlError}</p>
            )}
          </div>
        </>
      )}
      <div className="mt-3">
        <h3 className="text-center text-text-primary text-base font-medium">
          Enter Prompt
        </h3>
        <Input
          className={`${errors.prompt ? 'border-red-600' : 'border-secondary'} w-full mt-2 px-2 text-primary border bg-background border-opacity-[12.55%] rounded-md placeholder-text-placeholder outline-none`}
          placeholder="Please Enter Prompt"
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
        {errors.prompt && (
          <p className="text-red-500 text-xs">Please Enter Prompt</p>
        )}
      </div>
      <button
        onClick={handleGenerateGif}
        className={`w-full text-white rounded-lg h-12 flex items-center justify-center gap-2 mt-9 ${
          button !== 'Generate Gif' ? 'bg-secondary' : 'bg-primary'
        }`}
        disabled={button !== 'Generate Gif'}
      >
        {button === 'Generate Gif' ? (
          <Sparkle className="h-5 w-5" />
        ) : (
          <>
            <div className="animate-spin">
              <LoaderIcon size={20} />
            </div>
            {progress > 0 && (
              <div
                className="absolute bottom-0 left-0 h-1 bg-primary"
                style={{ width: `${progress}%` }}
              />
            )}
          </>
        )}
        <span className="text-lg font-medium">{button}</span>
      </button>
    </div>
  );
};

export default SelectImagesPanel;
